import React, { useState } from "react"
import { Button, Collapse, Dropdown, Menu, Modal, notification } from "antd"
import { DownOutlined, RightOutlined } from "@ant-design/icons"
import { useDispatch } from "react-redux"
import { Icon } from "components"
import { API, Endpoints } from "utils/api"
import Strings from "utils/strings"
import { setLoader } from "store/actions"
import ClassificationPresetSelector, {
	describeDimension,
} from "./ClassificationPresetSelector"

type BulkOrder = { _id: string; orderId: string; dimension?: string | null }
type BulkGroup = {
	bulkClassificationId: string
	dimension: string | null
	lot: string | null
	createdAt: string | null
	staff: string | null
	orders: BulkOrder[]
}
type InvalidOrder = {
	_id: string
	orderId: string | null
	reason: "NOT_BULK" | "NOT_PACKING" | "NOT_FOUND"
}
type Validation = {
	groups: BulkGroup[]
	invalid: InvalidOrder[]
	canApply: boolean
}
type Action = "undo" | "reclassify"

interface BulkClassificationActionsProps {
	selectedOrders: any[]
	setOrders: React.Dispatch<React.SetStateAction<any[]>>
	onSelectOrders: (orderIds: string[]) => void
}

const REASON_LABELS: Record<InvalidOrder["reason"], string> = {
	NOT_BULK: Strings.orders.bulkReasonNotBulk,
	NOT_PACKING: Strings.orders.bulkReasonNotPacking,
	NOT_FOUND: Strings.orders.bulkReasonNotFound,
}

const shortRunId = (id: string) => id.slice(-6).toUpperCase()

// Mirrors what the API writes, so the table updates without a refetch.
const classifiedFlow = (
	orderFlow: any,
	orderId: string,
	dimension: string | null
) => {
	if (!dimension) {
		return {
			...orderFlow,
			bagId: null,
			boxes: [],
			classificationSource: null,
			bulkClassificationId: null,
		}
	}
	return dimension.startsWith("#")
		? { ...orderFlow, bagId: `${dimension}${orderId}`, boxes: [] }
		: {
				...orderFlow,
				bagId: null,
				boxes: [{ id: `${dimension}${orderId}`, weight: 1, treated: false }],
			}
}

const listStyle: React.CSSProperties = {
	maxHeight: 220,
	overflowY: "auto",
	display: "flex",
	flexDirection: "column",
	gap: 4,
}
const rowStyle: React.CSSProperties = {
	display: "flex",
	justifyContent: "space-between",
	gap: 8,
}

const BulkClassificationActions: React.FC<BulkClassificationActionsProps> = ({
	selectedOrders,
	setOrders,
	onSelectOrders,
}) => {
	const dispatch = useDispatch()
	const [action, setAction] = useState<Action | null>(null)
	const [validation, setValidation] = useState<Validation | null>(null)
	const [dimension, setDimension] = useState<string>("#3")
	const [saving, setSaving] = useState(false)
	// Selection to apply once the modal closes (see selectOnlyGroup).
	const [pendingSelection, setPendingSelection] = useState<string[] | null>(
		null
	)

	// Controlled so the custom chevron knows which groups are open.
	const [expandedGroups, setExpandedGroups] = useState<string[]>([])
	const onGroupsToggle = (keys: string | string[]) =>
		setExpandedGroups(Array.isArray(keys) ? keys : [keys])

	const close = () => {
		setAction(null)
		setValidation(null)
		setExpandedGroups([])
		if (pendingSelection) {
			onSelectOrders(pendingSelection)
			setPendingSelection(null)
		}
	}

	const notifyError = (description?: string) =>
		notification.error({
			message: Strings.sidebar.orders,
			description: description || Strings.serverErrors.wentWrong,
			placement: "bottomRight",
			duration: 5,
		})

	const openAction = async (nextAction: Action) => {
		dispatch(setLoader(true))
		try {
			const response = await API.post({
				url: Endpoints.uriBulkClassification("validate"),
				data: { orderIds: selectedOrders.map((order) => order._id) },
			})
			if (!response.ok) return notifyError(response.data?.message)

			const result: Validation = response.data.results
			setValidation(result)
			setDimension(result.groups[0]?.dimension || "#3")
			setAction(nextAction)
		} catch {
			notifyError()
		} finally {
			dispatch(setLoader(false))
		}
	}

	// Narrows the selection to one group and continues the same action on it.
	// The group was just validated server-side, so it becomes the applicable
	// selection as-is; apply() still re-validates before writing.
	const selectOnlyGroup = (group: BulkGroup) => {
		const ids = group.orders.map((order) => order._id)
		setValidation({ groups: [group], invalid: [], canApply: true })
		setDimension(group.dimension || "#3")
		// With a single order the screen swaps this panel for the order detail
		// one, which would unmount the modal, so that selection waits for close.
		if (ids.length > 1) onSelectOrders(ids)
		else setPendingSelection(ids)
	}

	const apply = async () => {
		const group = validation?.groups[0]
		if (!action || !group) return

		setSaving(true)
		try {
			const response = await API.post({
				url: Endpoints.uriBulkClassification(action),
				data: {
					bulkClassificationId: group.bulkClassificationId,
					orderIds: group.orders.map((order) => order._id),
					...(action === "reclassify" ? { dimension } : {}),
				},
			})

			if (!response.ok) {
				// The selection changed since validation (e.g. an order was rescanned):
				// show the fresh state instead of a generic error.
				const fresh = response.data?.results?.validation
				if (fresh) setValidation(fresh)
				return notifyError(response.data?.message)
			}

			const newDimension = action === "reclassify" ? dimension : null
			const ids = new Set(group.orders.map((order) => order._id))
			setOrders((prev) =>
				prev.map((order) =>
					ids.has(order._id)
						? {
								...order,
								orderFlow: classifiedFlow(
									order.orderFlow,
									order.orderId,
									newDimension
								),
							}
						: order
				)
			)
			notification.success({
				message: Strings.sidebar.orders,
				description: Strings.orders.bulkUpdatedSuccess.replace(
					"{0}",
					String(response.data?.results?.modifiedCount ?? ids.size)
				),
				placement: "bottomRight",
				duration: 5,
			})
			close()
		} catch {
			notifyError()
		} finally {
			setSaving(false)
		}
	}

	const group = validation?.groups[0]
	const orderCount = group?.orders.length ?? 0

	// antd's own arrow is replaced by a chevron on the header's last line, next
	// to the "select only this group" shortcut.
	const renderGroupPanel = (g: BulkGroup, withShortcut: boolean) => (
		<Collapse.Panel
			key={g.bulkClassificationId}
			showArrow={false}
			header={
				<div>
					<strong>
						{shortRunId(g.bulkClassificationId)} ·{" "}
						{describeDimension(g.dimension)}
					</strong>
					<span style={{ marginLeft: 8 }}>
						{Strings.orders.bulkGroupOrders.replace(
							"{0}",
							String(g.orders.length)
						)}
					</span>
					<div style={{ color: "#8c8c8c", fontSize: 12 }}>
						{[
							g.staff,
							g.lot,
							g.createdAt && new Date(g.createdAt).toLocaleString(),
						]
							.filter(Boolean)
							.join(" · ")}
					</div>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginTop: 4,
						}}
					>
						<RightOutlined
							rotate={expandedGroups.includes(g.bulkClassificationId) ? 90 : 0}
							style={{ fontSize: 12, color: "#8c8c8c" }}
						/>
						{withShortcut && (
							<Button
								size="small"
								type="link"
								style={{ padding: 0 }}
								onClick={(e) => {
									// Don't toggle the panel when using the shortcut
									e.stopPropagation()
									selectOnlyGroup(g)
								}}
							>
								{Strings.orders.bulkSelectOnlyGroup}
							</Button>
						)}
					</div>
				</div>
			}
		>
			<div style={listStyle}>
				{g.orders.map((order) => (
					<div key={order._id} style={rowStyle}>
						<span>{order.orderId}</span>
						<span style={{ color: "#8c8c8c" }}>
							{describeDimension(order.dimension)}
						</span>
					</div>
				))}
			</div>
		</Collapse.Panel>
	)

	const invalidContent = validation && (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			<p style={{ margin: 0 }}>{Strings.orders.bulkMixedDescription}</p>
			{validation.groups.length > 0 && (
				<div>
					<strong>{Strings.orders.bulkValidGroups}</strong>
					<Collapse
						style={{ marginTop: 8 }}
						activeKey={expandedGroups}
						onChange={onGroupsToggle}
					>
						{validation.groups.map((g) => renderGroupPanel(g, true))}
					</Collapse>
				</div>
			)}
			{validation.invalid.length > 0 && (
				<Collapse>
					<Collapse.Panel
						key="invalid"
						header={
							<strong>
								{Strings.orders.bulkInvalidOrders} ({validation.invalid.length})
							</strong>
						}
					>
						<div style={listStyle}>
							{validation.invalid.map((order) => (
								<div key={order._id} style={rowStyle}>
									<span>{order.orderId || order._id}</span>
									<span style={{ color: "#cf1322" }}>
										{REASON_LABELS[order.reason]}
									</span>
								</div>
							))}
						</div>
					</Collapse.Panel>
				</Collapse>
			)}
		</div>
	)

	const applyContent = group && (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			<Collapse activeKey={expandedGroups} onChange={onGroupsToggle}>
				{renderGroupPanel(group, false)}
			</Collapse>
			{action === "undo" ? (
				<p style={{ margin: 0 }}>
					{Strings.orders.bulkUndoConfirm.replace("{0}", String(orderCount))}
				</p>
			) : (
				<ClassificationPresetSelector
					value={dimension}
					onChange={setDimension}
				/>
			)}
		</div>
	)

	const title = !validation?.canApply
		? Strings.orders.bulkMixedTitle
		: action === "undo"
			? Strings.orders.undoBulkClassification
			: Strings.orders.bulkReclassifyTitle.replace("{0}", String(orderCount))

	return (
		<>
			<Dropdown
				trigger={["click"]}
				overlay={
					<Menu onClick={({ key }) => openAction(key as Action)}>
						<Menu.Item key="reclassify">
							{Strings.orders.reclassifyBulk}
						</Menu.Item>
						<Menu.Item key="undo" danger>
							{Strings.orders.undoBulkClassification}
						</Menu.Item>
					</Menu>
				}
			>
				<div className="OrderDetailPanel__actions-item">
					<Icon name="package" />
					<span>
						{Strings.orders.bulkClassificationLabel}{" "}
						<DownOutlined style={{ fontSize: 9 }} />
					</span>
				</div>
			</Dropdown>

			<Modal
				title={title}
				visible={!!action && !!validation}
				onCancel={close}
				footer={
					validation?.canApply
						? [
								<Button key="cancel" onClick={close}>
									{Strings.generic.cancel}
								</Button>,
								<Button
									key="apply"
									type="primary"
									danger={action === "undo"}
									loading={saving}
									onClick={apply}
								>
									{action === "undo"
										? Strings.orders.undoBulkClassification
										: Strings.orders.reclassifyBulk}
								</Button>,
							]
						: [
								<Button key="close" onClick={close}>
									{Strings.generic.cancel}
								</Button>,
							]
				}
			>
				{validation?.canApply ? applyContent : invalidContent}
			</Modal>
		</>
	)
}

export default BulkClassificationActions
